import { useCallback } from 'react';
import { useAuthStore, useModalStore, useToastStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useTranslation } from '@/hooks/useTranslation';

export default function useFavoriteAgent(agentId) {
  const { t } = useTranslation();
  const requireAuth = useRequireAuth();
  const { openModal } = useModalStore((state) => state.actions);
  const { addToast } = useToastStore((state) => state.actions);
  const toggleFavoriteAgent = useAuthStore((state) => state.toggleFavoriteAgent);
  const favoriteAgentIds = useAuthStore((state) => state.user?.favoriteAgentIds) || [];
  const isGhost = useAuthStore((state) => state.user?.flags?.isGhost);
  const isPlus = useAuthStore((state) => state.user?.flags?.isPlus);

  const isSaved = !!agentId && favoriteAgentIds.includes(agentId);
  const favoriteAgentsCount = favoriteAgentIds.length;

  const toggleFavorite = useCallback(async (event) => {
    event?.stopPropagation?.();
    if (typeof event?.preventDefault === 'function') event.preventDefault();

    if (!agentId) return false;

    return requireAuth(async () => {
      try {
        if (isGhost && !isSaved && favoriteAgentsCount >= 1) {
          openModal('GhostSignupModal');
          return false;
        }
        if (!isPlus && !isSaved && favoriteAgentsCount >= 5) {
          openModal('PlusFeatureModal', { feature: 'favorite-bots' });
          return false;
        }

        const success = await toggleFavoriteAgent(agentId);
        if (success) {
          addToast(
            isSaved
              ? (t('nav.routes.agentChat.removedFromFavorites') || 'Agent removed from favorites')
              : (t('nav.routes.agentChat.addedToFavorites') || 'Agent added to favorites'),
            'success',
          );
        } else {
          addToast(
            isSaved
              ? (t('nav.routes.agentChat.failedToUnsave') || 'Failed to unsave agent')
              : (t('nav.routes.agentChat.failedToSave') || 'Failed to save agent'),
            'error',
          );
        }
        return success;
      } catch (error) {
        if (error?.name === 'ApiError' && error?.message) {
          addToast(error.message, 'error', error?.data?.actions);
        } else {
          addToast(
            isSaved
              ? (t('nav.routes.agentChat.failedToUnsave') || 'Failed to unsave agent')
              : (t('nav.routes.agentChat.failedToSave') || 'Failed to save agent'),
            'error',
          );
        }
        return false;
      }
    }, {
      source: 'favorite_agent',
      title: t('pages.agents.signInToFavorite') || 'Sign in to favorite an agent',
      subtitle: t('pages.agents.signInToFavoriteSubtitle')
        || 'Favorites appear in your sidebar so you can start a new chat with that agent anytime.',
      icon: 'star',
    });
  }, [
    agentId,
    addToast,
    favoriteAgentsCount,
    isGhost,
    isPlus,
    isSaved,
    openModal,
    requireAuth,
    t,
    toggleFavoriteAgent,
  ]);

  return { isSaved, toggleFavorite };
}
